import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { _cartUI, cartUI } from '../../../shared/store/cart.store.js'

export interface CartItemModifier {
  id: string
  name: string
  price: number
  quantity: number
}

export interface CartItemDisplay {
  id: string
  name: string
  variantName: string | null
  imageUrl: string | null
  quantity: number
  unitPrice: number
  modifiers: CartItemModifier[]
  totalPrice: number
}

export interface CartDisplay {
  id: string
  items: CartItemDisplay[]
  total: number
}

interface Props {
  cart: CartDisplay | null
  loading: boolean
  error: string | null
  onUpdateQty: (itemId: string, qty: number) => void
  onRemove: (itemId: string) => void
  onClear: () => void
  onCheckout: () => void
  orderingBlocked?: boolean
  blockedMessage?: string | null
}

export default function CartDrawer({
  cart, loading, error, onUpdateQty, onRemove, onClear, onCheckout,
  orderingBlocked = false, blockedMessage = null,
}: Props) {
  const { t } = useTranslation()
  const isOpen = _cartUI(s => s.isOpen)

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') cartUI.close()
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div className="storefront-cart-overlay" onClick={() => cartUI.close()} />

      {/* Drawer */}
      <div className="storefront-cart-drawer">
        <div className="storefront-cart-header">
          <h2>{t('cart.cart')}</h2>
          <button type="button" className="storefront-cart-close" onClick={() => cartUI.close()}>
            &times;
          </button>
        </div>

        <div className="storefront-cart-body">
          {loading && (
            <div className="storefront-cart-loading">{t('cart.loading')}</div>
          )}

          {error && (
            <div className="storefront-cart-error">{error}</div>
          )}

          {!loading && !error && (!cart || cart.items.length === 0) && (
            <div className="storefront-cart-empty">{t('cart.empty')}</div>
          )}

          {!loading && !error && cart && cart.items.length > 0 && (
            <ul className="storefront-cart-items">
              {cart.items.map(item => (
                <li key={item.id} className="storefront-cart-item">
                  <span className="storefront-cart-item-thumb">
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt="" className="storefront-cart-item-img" />
                      : <span className="storefront-cart-item-placeholder" />}
                  </span>
                  <div className="storefront-cart-item-info">
                    <p className="storefront-cart-item-name">
                      {item.name}
                      {item.variantName && <span className="storefront-cart-item-variant">{item.variantName}</span>}
                    </p>
                    {item.modifiers.length > 0 && (
                      <ul className="storefront-cart-item-mods">
                        {item.modifiers.map(m => (
                          <li key={m.id} className="storefront-cart-item-mod">
                            {m.name} <span className="storefront-cart-mod-price">+{m.price} ₽</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="storefront-cart-item-qty">
                    <button type="button" className="storefront-qty-btn" onClick={() => onUpdateQty(item.id, item.quantity - 1)}>
                      &minus;
                    </button>
                    <span className="storefront-qty-value">{item.quantity}</span>
                    <button
                      type="button"
                      className="storefront-qty-btn"
                      disabled={orderingBlocked}
                      title={orderingBlocked ? blockedMessage ?? undefined : undefined}
                      onClick={() => onUpdateQty(item.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <div className="storefront-cart-item-total">
                    <span>{item.totalPrice} ₽</span>
                  </div>
                  <button type="button" className="storefront-cart-item-remove" onClick={() => onRemove(item.id)}>
                    &times;
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {!loading && !error && cart && cart.items.length > 0 && (
          <div className="storefront-cart-footer">
            <div className="storefront-cart-total">
              <span>{t('cart.total')}</span>
              <span>{cart.total} ₽</span>
            </div>
            <button type="button" className="storefront-cart-checkout" onClick={onCheckout}>
              {t('checkout.submit')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClear}>
              {t('cart.clear')}
            </button>
          </div>
        )}
      </div>
    </>
  )
}